// Drawing App - IndexedDB Persistence

import { BrushSettings, ViewTransform } from './types';

export interface LayerSaveData {
  id: string;
  name: string;
  opacity: number;
  blendMode: GlobalCompositeOperation;
  visible: boolean;
  imageDataURL: string;
}

export interface DrawingSaveData {
  layers: LayerSaveData[];
  activeLayerId: string;
  viewTransform: ViewTransform;
  brushSettings: BrushSettings;
  primaryColor: string;
  savedAt: number;
}

const DB_NAME = 'layer-drawing-db';
const DB_VERSION = 1;
const STORE_NAME = 'drawings';

export class DrawingStorage {
  private db: IDBDatabase | null = null;
  private saveTimer: number | null = null;
  private dirty = false;
  private onDirtyChange: ((dirty: boolean) => void) | null = null;
  private onSaveStatusChange: ((status: 'saved' | 'saving' | 'dirty') => void) | null = null;

  async open(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'roomCode' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onerror = () => {
        console.error('[DrawingStorage] Failed to open IndexedDB');
        reject(request.error);
      };
    });
  }

  async saveState(roomCode: string, data: DrawingSaveData): Promise<void> {
    if (!this.db) await this.open();
    if (!this.db) return;

    this.notifyStatus('saving');

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({ roomCode, ...data });

      tx.oncomplete = () => {
        this.dirty = false;
        this.notifyStatus('saved');
        if (this.onDirtyChange) this.onDirtyChange(false);
        resolve();
      };

      tx.onerror = () => {
        console.error('[DrawingStorage] Save failed');
        reject(tx.error);
      };
    });
  }

  async loadState(roomCode: string): Promise<DrawingSaveData | null> {
    if (!this.db) await this.open();
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(roomCode);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const { roomCode: _, ...data } = result;
          resolve(data as DrawingSaveData);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('[DrawingStorage] Load failed');
        reject(request.error);
      };
    });
  }

  async deleteState(roomCode: string): Promise<void> {
    if (!this.db) await this.open();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(roomCode);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  markDirty(): void {
    if (!this.dirty) {
      this.dirty = true;
      this.notifyStatus('dirty');
      if (this.onDirtyChange) this.onDirtyChange(true);
    }
  }

  isDirty(): boolean {
    return this.dirty;
  }

  scheduleSave(roomCode: string, getState: () => DrawingSaveData, delayMs = 5000): void {
    this.markDirty();
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
    }
    this.saveTimer = window.setTimeout(async () => {
      this.saveTimer = null;
      try {
        const data = getState();
        await this.saveState(roomCode, data);
      } catch (e) {
        console.error('[DrawingStorage] Auto-save failed:', e);
      }
    }, delayMs);
  }

  async saveNow(roomCode: string, getState: () => DrawingSaveData): Promise<void> {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    if (this.dirty) {
      try {
        const data = getState();
        await this.saveState(roomCode, data);
      } catch (e) {
        console.error('[DrawingStorage] Immediate save failed:', e);
      }
    }
  }

  onDirty(cb: (dirty: boolean) => void): void {
    this.onDirtyChange = cb;
  }

  onStatusChange(cb: (status: 'saved' | 'saving' | 'dirty') => void): void {
    this.onSaveStatusChange = cb;
  }

  private notifyStatus(status: 'saved' | 'saving' | 'dirty'): void {
    if (this.onSaveStatusChange) this.onSaveStatusChange(status);
  }

  destroy(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}
